import styled from "styled-components";
import { Box, darken, lighten } from "@mui/material";
import { TemplateColorObject } from "../../../../../InvitationColorMatrix";

const SWING = "2.4s cubic-bezier(0.65, 0, 0.35, 1)";

export const PalaceDoorsEntranceWrapper = styled(Box)<{ colormatrix: TemplateColorObject }>`
	position: absolute;
	z-index: 3;
	width: 100%;
	max-width: 430px;
	height: 100vh;
	overflow: hidden;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: opacity 1.2s ease-in-out 3.6s;
	background: ${props => props.colormatrix.entrance.background};

	&.show {
		opacity: 1;
		cursor: pointer;
	}

	&.hide {
		opacity: 0;
		z-index: -1;
		cursor: unset;
	}

	&.preview {
		position: relative;
		max-width: 320px;
		height: 600px;
		border-radius: 16px;
		margin: 0 auto;
		cursor: default;
	}

	/* The arched palace doorway. Leaves swing inward and are clipped by the arch. */
	.doorway {
		position: relative;
		width: min(78%, 330px);
		height: min(66vh, 560px);
		margin-top: -6%;
		border-radius: 999px 999px 0 0;
		overflow: hidden;
		isolation: isolate;
		perspective: 1100px;
		background: ${props => darken(props.colormatrix.entrance.envelope_background, 0.55)};
		box-shadow:
			0 0 0 6px ${props => lighten(props.colormatrix.entrance.envelope_background, 0.35)},
			0 0 0 9px ${props => darken(props.colormatrix.entrance.envelope_background, 0.35)},
			0 28px 50px rgba(0, 0, 0, 0.35);
	}

	&.preview .doorway {
		height: 440px;
	}

	.light {
		position: absolute;
		inset: 0;
		z-index: 0;
		opacity: 0;
		background: radial-gradient(ellipse at 50% 55%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 65%),
			linear-gradient(
				180deg,
				${props => lighten(props.colormatrix.entrance.envelope_background, 0.75)},
				${props => lighten(props.colormatrix.entrance.envelope_background, 0.9)}
			);
		transition: opacity 1.4s ease-out 0.1s;
	}
	&.opened .light {
		opacity: 1;
	}

	.couples_banner {
		position: absolute;
		inset: 0;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 0 22px;
		text-align: center;

		p {
			text-align: center;
			color: ${props => darken(props.colormatrix.entrance.envelope_background, 0.55)};
		}
		.wedding_of {
			font-family: var(--roboto), sans-serif;
			font-size: 16px;
			opacity: 0;
		}
		.couples {
			font-family: var(--inriaSerif), sans-serif;
			font-size: 26px;
			opacity: 0;
		}
	}

	&.opened .wedding_of {
		animation: palace_name_drop 1s ease-out 1.2s forwards;
	}
	&.opened .groom {
		animation: palace_name_in_start 1.1s cubic-bezier(0.22, 1, 0.36, 1) 1.5s forwards;
	}
	&.opened .and {
		animation: palace_name_pop 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 1.9s forwards;
	}
	&.opened .bride {
		animation: palace_name_in_end 1.1s cubic-bezier(0.22, 1, 0.36, 1) 2.2s forwards;
	}

	.doors {
		position: absolute;
		inset: 0;
		z-index: 2;
		display: flex;
		transform-style: preserve-3d;
	}

	.leaf {
		position: relative;
		width: 50%;
		height: 100%;
		box-sizing: border-box;
		backface-visibility: hidden;
		background: linear-gradient(
			90deg,
			${props => darken(props.colormatrix.entrance.envelope_background, 0.5)},
			${props => darken(props.colormatrix.entrance.envelope_background, 0.28)} 45%,
			${props => darken(props.colormatrix.entrance.envelope_background, 0.42)}
		);
		transition: transform ${SWING};

		&.left {
			transform-origin: left center;
			border-right: 1px solid rgba(0, 0, 0, 0.55);
		}
		&.right {
			transform-origin: right center;
			border-left: 1px solid rgba(0, 0, 0, 0.55);
			background-image: linear-gradient(
				270deg,
				${props => darken(props.colormatrix.entrance.envelope_background, 0.5)},
				${props => darken(props.colormatrix.entrance.envelope_background, 0.28)} 45%,
				${props => darken(props.colormatrix.entrance.envelope_background, 0.42)}
			);
		}
	}
	&.opened .leaf.left {
		transform: rotateY(98deg);
	}
	&.opened .leaf.right {
		transform: rotateY(-98deg);
	}

	/* Raised gilded panels. The arched top panel follows the doorway curve. */
	.panel {
		position: absolute;
		left: 12%;
		right: 12%;
		border: 2px solid ${props => lighten(props.colormatrix.entrance.envelope_background, 0.35)};
		box-shadow:
			inset 0 0 0 4px ${props => darken(props.colormatrix.entrance.envelope_background, 0.4)},
			inset 0 0 0 5px ${props => lighten(props.colormatrix.entrance.envelope_background, 0.2)},
			0 2px 6px rgba(0, 0, 0, 0.4);
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(0, 0, 0, 0.12));
	}
	.panel_top {
		top: 14%;
		height: 42%;
		border-radius: 999px 999px 4px 4px;
	}
	.panel_bottom {
		top: 60%;
		height: 28%;
		border-radius: 4px;
	}
	.leaf.right .panel_top,
	.leaf.right .panel_bottom {
		left: 12%;
		right: 12%;
	}

	.studs {
		position: absolute;
		top: 4%;
		bottom: 4%;
		width: 8px;
		background: radial-gradient(
				circle,
				${props => lighten(props.colormatrix.entrance.envelope_background, 0.55)} 1.5px,
				transparent 2.5px
			)
			center top / 8px 22px repeat-y;
	}
	.leaf.left .studs {
		left: 4px;
	}
	.leaf.right .studs {
		right: 4px;
	}

	.handle {
		position: absolute;
		top: 54%;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		border: 3px solid ${props => lighten(props.colormatrix.entrance.envelope_background, 0.5)};
		box-shadow:
			0 2px 4px rgba(0, 0, 0, 0.5),
			inset 0 1px 2px rgba(0, 0, 0, 0.4);
		background: radial-gradient(
			circle at 35% 30%,
			${props => lighten(props.colormatrix.entrance.envelope_background, 0.7)},
			${props => darken(props.colormatrix.entrance.envelope_background, 0.1)}
		);
	}
	.leaf.left .handle {
		right: 10px;
	}
	.leaf.right .handle {
		left: 10px;
	}

	.tab_continue_container {
		position: absolute;
		bottom: 4%;
		left: 0;
		right: 0;
		z-index: 4;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 15px;

		.tab_continue {
			background: rgba(255, 255, 255, 0.85);
			box-shadow: 0 0 8px 1px ${props => darken(props.colormatrix.entrance.envelope_background, 0.3)};
			border-radius: 50px;
			padding: 20px;

			svg {
				fill: ${props => darken(props.colormatrix.entrance.envelope_background, 0.4)};
			}
		}

		.tab_continue_text {
			font-family: var(--poppins), sans-serif;
			font-weight: bold;
			color: ${props => darken(props.colormatrix.entrance.envelope_background, 0.5)};
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.leaf {
			transition: opacity 0.6s ease;
		}
		&.opened .leaf.left,
		&.opened .leaf.right {
			transform: none;
			opacity: 0;
		}
	}

	@keyframes palace_name_drop {
		from {
			opacity: 0;
			transform: translateY(-14px);
			letter-spacing: 6px;
		}
		to {
			opacity: 1;
			transform: translateY(0);
			letter-spacing: normal;
		}
	}
	@keyframes palace_name_in_start {
		from {
			opacity: 0;
			transform: translateX(-40px) scale(0.9);
			filter: blur(6px);
		}
		to {
			opacity: 1;
			transform: translateX(0) scale(1);
			filter: blur(0);
		}
	}
	@keyframes palace_name_in_end {
		from {
			opacity: 0;
			transform: translateX(40px) scale(0.9);
			filter: blur(6px);
		}
		to {
			opacity: 1;
			transform: translateX(0) scale(1);
			filter: blur(0);
		}
	}
	@keyframes palace_name_pop {
		from {
			opacity: 0;
			transform: scale(0.2) rotate(-20deg);
		}
		to {
			opacity: 1;
			transform: scale(1) rotate(0);
		}
	}
`;
